import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import type { User } from '@/types/user'

function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: number | null = null;
  
  return function(...args: Parameters<T>) {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait) as unknown as number;
  };
}

export const useUsersStore = defineStore('users', () => {
  const users = ref<User[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const totalCount = ref(0)
  const nextPage = ref<string | null>(null)
  const prevPage = ref<string | null>(null)
  const currentPage = ref(1)
  const searchQuery = ref('')
  const selectedRole = ref('')
  const selectedUser = ref<User | null>(null)
  const guides = ref<User[]>([])
  const guidesLoading = ref(false)
  const guidesError = ref<string | null>(null)
  const guidesAndMentors = ref<User[]>([])
  const guidesAndMentorsLoading = ref(false)
  const guidesAndMentorsError = ref<string | null>(null)

  const fetchUsers = async (params?: { 
    page?: number;
    search?: string;
    role?: string;
    first?: number;
    rows?: number;
    page_size?: number;
    ordering?: string;
  }) => {
    isLoading.value = true
    error.value = null
    
    try {
      const response = await api.get(API_ROUTES.GET_USERS, {
        params: {
          page: params?.page || currentPage.value,
          search: params?.search || searchQuery.value,
          role: params?.role || selectedRole.value,
          page_size: params?.page_size || 100,
          ordering: params?.ordering || null,
        }
      })
      
      users.value = response.data.results
      nextPage.value = response.data.next
      prevPage.value = response.data.previous
      totalCount.value = response.data.count
      return response.data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch users'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const loadNextPage = async () => {
    if (nextPage.value && !isLoading.value) {
      currentPage.value++
      await fetchUsers()
    }
  }

  const loadPrevPage = async () => {
    if (prevPage.value && !isLoading.value) {
      currentPage.value--
      await fetchUsers()
    }
  }

  const debouncedSearch = debounce(async () => {
    currentPage.value = 1
    await fetchUsers()
  }, 300)

  const handleRoleChange = async () => {
    currentPage.value = 1
    await fetchUsers()
  }

  const getUserFromReferral = (referral: any) => {
    return referral?.user_details || null
  }

  function setSelectedUser(referral: any) {
    selectedUser.value = getUserFromReferral(referral)
  }

  const fetchAllUsers = async () => {
    isLoading.value = true
    error.value = null
    
    try {
      const response = await api.get(API_ROUTES.GET_USERS, {
        params: {
          page_size: 1000  // Set a large number to get all users at once
        }
      })
      
      users.value = response.data.results
      totalCount.value = response.data.count
      return response.data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch users'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const fetchUserById = async (userId: string | number) => {
    isLoading.value = true;
    error.value = null;
    selectedUser.value = null; // Reset selected user before fetching

    try {
      const response = await api.get(`${API_ROUTES.GET_USERS}${userId}/`);
      selectedUser.value = response.data;
      return response.data;
    } catch (err: any) {
      console.error("Failed to fetch user by ID:", err);
      error.value = err.response?.data?.message || `Failed to fetch user ${userId}`;
      selectedUser.value = null; // Ensure selectedUser is null on error
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchGuides = async () => {
    guidesLoading.value = true
    guidesError.value = null
    
    try {
      const response = await api.get(API_ROUTES.GET_ALL_GUIDES)
      guides.value = response.data
      return response.data
    } catch (err: any) {
      console.error("Failed to fetch guides:", err);
      guidesError.value = err.response?.data?.message || 'Failed to fetch guides'
      throw err
    } finally {
      guidesLoading.value = false
    }
  }

  const fetchGuidesAndMentors = async () => {
    guidesAndMentorsLoading.value = true
    guidesAndMentorsError.value = null
    
    try {
      const response = await api.get(API_ROUTES.GET_GUIDES_AND_MENTORS)
      guidesAndMentors.value = response.data
      return response.data
    } catch (err: any) {
      console.error("Failed to fetch guides and mentors:", err);
      guidesAndMentorsError.value = err.response?.data?.message || 'Failed to fetch guides and mentors'
      throw err
    } finally {
      guidesAndMentorsLoading.value = false
    }
  }

  return {
    users,
    isLoading,
    error,
    totalCount,
    nextPage,
    prevPage,
    currentPage,
    searchQuery,
    selectedRole,
    selectedUser,
    fetchUsers,
    loadNextPage,
    loadPrevPage,
    debouncedSearch,
    handleRoleChange,
    getUserFromReferral,
    setSelectedUser,
    fetchAllUsers,
    fetchUserById,
    fetchGuides,
    guides,
    guidesLoading,
    guidesError,
    fetchGuidesAndMentors,
    guidesAndMentors,
    guidesAndMentorsLoading,
    guidesAndMentorsError
  }
})
